import React, { useState } from "react";

import { DragEndEvent, useDndMonitor } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Add, GroupsOutlined } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { EntityCard } from "@/components/alerts/add-v2/entities/EntityCard";
import { EntityEditor } from "@/components/alerts/add-v2/entities/EntityEditor";
import { SortableEntity } from "@/components/alerts/add-v2/entities/SortableEntity";
import { AttachOption } from "@/components/alerts/add-v2/media/AttachMenu";
import { mediaChipIcon } from "@/components/alerts/add-v2/media/mediaChipIcon";
import { useMediaUploads } from "@/components/alerts/add-v2/media/useMediaUploads";
import { createEntity } from "@/components/alerts/add-v2/model/defaults";
import { dropIntent } from "@/components/alerts/add-v2/model/dropIntent";
import {
  entityDraftHasContent,
  numberWithinType,
  validateEntityDraft,
} from "@/components/alerts/add-v2/model/entityRules";
import { detachEntity } from "@/components/alerts/add-v2/model/mediaLinks";
import {
  BulletinEntity,
  EntityType,
} from "@/components/alerts/add-v2/model/types";
import { useBulletinField } from "@/components/alerts/add-v2/state/bulletinFormContext";

/**
 * People, vehicles and items — the section this rewrite exists for.
 *
 * ## When it saves
 *
 * There is no save. Every field writes straight into `values.entities` as it is typed, the same as
 * `ContactRow`, `LocationRow` and `IncidentTimeRow`, and `useAutosave` sends the document 800 ms
 * later or on the next blur, whichever comes first.
 *
 * A row is therefore in the document from the moment **Add** is clicked. Two consequences worth
 * knowing before changing anything here:
 *
 * - **The id is stable from creation.** Media items point at entities by id (decision 13), so an
 *   attachment made while the card is open stays valid — there is no later commit to renumber it.
 * - **An empty row cleans itself up.** Clicking Add and walking away would otherwise leave a blank
 *   entity in the payload, so collapsing a row that is still untouched deletes it instead. That is
 *   the same idea as `normalizeContactRows`' trailing blank, applied at collapse rather than on
 *   every keystroke, because an entity has no trailing row to type into.
 *
 * Seeding placeholders from the bulletin type (`autoAdded`) is specified by the design and is
 * deliberately **not built yet**; the model field and the card's badge are in place for it.
 *
 * ## Order
 *
 * The list is drag-sortable, and the array's own order **is** the order — it round-trips through the
 * document (`adapters.ts` maps `entities` position for position) and it is what `numberWithinType`
 * counts, so moving a card renumbers "Person #2" into "Person #1" everywhere the label is shown.
 */
export const EntitiesSection: React.FC = () => {
  const { t } = useTranslation("bulletin/create-v2");
  const [entities, setEntities] = useBulletinField("entities");
  // The entity/media relation is stored on the media items (decision 8), so attaching from this side
  // writes there — the entity itself never gains a list.
  const [media, setMedia] = useBulletinField("media");
  // The write behind "attach media" and behind dropping a file on a card: the relation is stored on
  // the server now (decision 43), so it is a call and not a `setMedia`.
  const uploads = useMediaUploads();
  // Read for the conditional identity rules only — first name, last name and DOB become required on
  // the bulletin types listed in `IDENTITY_REQUIRED_BULLETIN_TYPES`.
  const [bulletinType] = useBulletinField("bulletinType");
  /** Which row is expanded. One at a time: the design shows a single inline form. */
  const [openId, setOpenId] = useState<string | null>(null);

  /** Drops `id` if it is still an untouched blank. Runs whenever a row stops being the open one. */
  const withoutEmpty = (rows: BulletinEntity[], id: string | null) =>
    rows.filter(
      (row) => row.id !== id || entityDraftHasContent(row) || row.autoAdded,
    );

  const add = () => {
    const entity = createEntity("person");
    setEntities([...withoutEmpty(entities, openId), entity]);
    setOpenId(entity.id);
  };

  const open = (entity: BulletinEntity) => {
    setEntities(withoutEmpty(entities, openId));
    setOpenId(entity.id);
  };

  const collapse = () => {
    setEntities(withoutEmpty(entities, openId));
    setOpenId(null);
  };

  /**
   * One `arrayMove` and nothing else: order is not a field, so there is no `sort_ordinal` to PATCH
   * the way media has one — the idle autosave sends the reordered document like any other change.
   *
   * The drag context is the page's, because the same gesture crosses into the media section. This
   * section therefore handles only the drags that *start* on an entity, and ignores the rest.
   */
  useDndMonitor({
    onDragEnd: ({ active, over }: DragEndEvent) => {
      const intent = dropIntent(active, over);
      if (intent?.kind !== "reorder-entities") return;
      setEntities(
        arrayMove(
          entities,
          entities.findIndex((row) => row.id === intent.activeId),
          entities.findIndex((row) => row.id === intent.overId),
        ),
      );
    },
  });

  const update = (entity: BulletinEntity) =>
    setEntities(entities.map((row) => (row.id === entity.id ? entity : row)));

  /**
   * Rebuilds the row rather than mutating it — decision 10, and what keeps a person's date of birth
   * from surviving into a vehicle. The id is carried over so media links and the open state hold.
   */
  const changeType = (entity: BulletinEntity, type: EntityType) =>
    update({ ...createEntity(type), id: entity.id });

  const remove = (entity: BulletinEntity) => {
    setEntities(entities.filter((row) => row.id !== entity.id));
    // Nothing else drops the link: an id left behind on a media item would ride the payload
    // pointing at an entity that no longer exists.
    setMedia((previous) => detachEntity(previous, entity.id));
    if (openId === entity.id) setOpenId(null);
  };

  /**
   * What is missing on a row — empty while the row is still an untouched blank.
   *
   * That gate is not a display preference: `validation/schemas.ts` skips exactly the same rows, so
   * clicking Add cannot make "N required fields left" jump by three before anything is typed. A
   * seeded placeholder (`autoAdded`) is empty by definition and is still meant to be filled in, so
   * it does complain.
   */
  const errorsFor = (entity: BulletinEntity) =>
    entityDraftHasContent(entity) || entity.autoAdded
      ? validateEntityDraft(entity, bulletinType)
      : {};

  const mediaOptions = (entity: BulletinEntity): AttachOption[] =>
    media.map((item) => ({
      id: item.id,
      label: item.filename,
      icon: mediaChipIcon(item.contentType),
      checked: item.entityIds.includes(entity.id),
    }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {entities.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.75,
            py: 2.25,
            color: "text.secondary",
          }}
        >
          <GroupsOutlined sx={{ fontSize: 28, color: "action.disabled" }} />
          <Typography variant="body2">{t("entities.empty")}</Typography>
        </Box>
      ) : (
        <SortableContext
          items={entities.map((entity) => entity.id)}
          strategy={verticalListSortingStrategy}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {entities.map((entity) => (
              <SortableEntity key={entity.id} id={entity.id}>
                {(handle, mediaOver) =>
                  entity.id === openId ? (
                    <EntityEditor
                      entity={entity}
                      index={numberWithinType(entities, entity)}
                      bulletinType={bulletinType}
                      errors={errorsFor(entity)}
                      onChange={update}
                      onChangeType={(type) => changeType(entity, type)}
                      onDone={collapse}
                      onDelete={() => remove(entity)}
                    />
                  ) : (
                    <EntityCard
                      entity={entity}
                      index={numberWithinType(entities, entity)}
                      mediaOptions={mediaOptions(entity)}
                      // A collapsed card is the only thing on screen for that entity, so it has
                      // to carry the fact that it is incomplete — otherwise the jump lands on a
                      // section that looks finished.
                      incomplete={Object.keys(errorsFor(entity)).length > 0}
                      mediaOver={mediaOver}
                      dragHandle={handle}
                      onEdit={() => open(entity)}
                      onDelete={() => remove(entity)}
                      onToggleMedia={(mediaId) =>
                        uploads.toggleEntity(mediaId, entity.id)
                      }
                    />
                  )
                }
              </SortableEntity>
            ))}
          </Box>
        </SortableContext>
      )}

      <Box sx={{ display: "flex" }}>
        <Button variant="outlined" startIcon={<Add />} onClick={add}>
          {t("entities.add_entity")}
        </Button>
      </Box>
    </Box>
  );
};
